import { useNavigate } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Lead, Priority } from "@/mocks/leadsData";
import { priorityDotColors } from "@/mocks/leadsData";

type Props = {
  deal: Lead;
  isDragOverlay?: boolean;
};

export function DealCard({ deal, isDragOverlay = false }: Props) {
  const navigate = useNavigate();
  const assignedName = deal.assigned || "Unassigned";
  const initials = assignedName
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "U";

  // Calculate days in current stage dynamically
  const getDaysInStage = (): number => {
    if (deal.lastActivity) {
      const parsed = Date.parse(deal.lastActivity);
      if (!isNaN(parsed)) {
        const diffMs = Date.now() - parsed;
        return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
      }
      if (deal.lastActivity.includes("hour") || deal.lastActivity.includes("min") || deal.lastActivity.includes("Just")) {
        return 1;
      }
      const match = deal.lastActivity.match(/(\d+)\s*day/);
      if (match) return parseInt(match[1], 10);
    }
    return 1;
  };

  const daysInStage = getDaysInStage();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: deal.id,
    data: { type: "deal", deal },
    disabled: isDragOverlay,
  });

  const style = isDragOverlay
    ? {}
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={(e) => {
        if (isDragging) return;
        navigate(`/leads/${deal.id}`);
      }}
      className={`bg-white border border-gray-200 rounded-xl p-3.5 mb-3 cursor-pointer hover:border-indigo-300 hover:shadow-sm transition-all duration-200 group relative ${
        isDragOverlay ? "!border-2 !border-indigo-400 !shadow-xl !scale-[1.02] !opacity-95 !mb-0 !cursor-grabbing" : ""
      }`}
    >
      {/* Drag handle — 6-dot grid icon, top-left */}
      <div
        {...listeners}
        className="absolute top-2 left-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-grab hover:bg-gray-100"
        onClick={(e) => e.stopPropagation()}
        title="Drag to move"
      >
        <i className="ri-drag-move-2-line text-xs text-gray-400" />
      </div>

      <div className="flex items-start justify-between mb-1.5 pl-5">
        <p className="font-semibold text-gray-900 text-sm truncate pr-2">{deal.company || deal.name}</p>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${priorityDotColors[deal.priority as Priority] || "bg-gray-400"}`} />
      </div>
      <p className="text-sm text-gray-500 truncate mb-2 pl-5">{deal.name}</p>
      <p className="font-bold text-indigo-600 text-base mb-3 pl-5">{deal.value || "₹0"}</p>
      <div className="flex items-center justify-between pl-5">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[9px] font-bold text-indigo-700">
            {initials}
          </div>
          <span className="text-xs text-gray-500 truncate max-w-[90px]">{assignedName}</span>
        </div>
        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 whitespace-nowrap">
          {daysInStage}d
        </span>
      </div>
    </div>
  );
}